/**
 * Client API moko-aviation-api.
 *
 * Auth: JWT Bearer dans Authorization header. Access en mémoire, refresh
 * en sessionStorage (résiste au reload mais pas à fermeture onglet).
 * Erreurs: format RFC 7807.
 */

const API_BASE = import.meta.env.VITE_API_URL ?? "https://api-staging.aviation.gofreshpay.com";

let _accessToken: string | null = null;
let _refreshToken: string | null = null;

export function setTokens(access: string, refresh: string): void {
  _accessToken = access;
  _refreshToken = refresh;
  sessionStorage.setItem("moko_refresh", refresh);
}

export function clearTokens(): void {
  _accessToken = null;
  _refreshToken = null;
  sessionStorage.removeItem("moko_refresh");
}

export function getAccessToken(): string | null {
  return _accessToken;
}

export function getRefreshToken(): string | null {
  if (_refreshToken) return _refreshToken;
  const stored = sessionStorage.getItem("moko_refresh");
  if (stored) {
    _refreshToken = stored;
    return stored;
  }
  return null;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public type: string,
    public title: string,
    public detail: string,
    public requestId?: string,
    public extra?: Record<string, unknown>,
  ) {
    super(detail || title);
    this.name = "ApiError";
  }
}

type RequestOptions = {
  method?: string;
  body?: unknown;
  skipAuth?: boolean;
  headers?: Record<string, string>;
  query?: Record<string, string | number | boolean | undefined | null>;
};

function buildQueryString(query?: Record<string, unknown>): string {
  if (!query) return "";
  const parts: string[] = [];
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null || v === "") continue;
    parts.push(`${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`);
  }
  return parts.length ? `?${parts.join("&")}` : "";
}

export async function apiRequest<T = unknown>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const { method = "GET", body, skipAuth = false, headers = {}, query } = options;

  const finalHeaders: Record<string, string> = {
    Accept: "application/json",
    ...headers,
  };

  const isFormData = typeof FormData !== "undefined" && body instanceof FormData;

  if (body !== undefined && !isFormData) {
    finalHeaders["Content-Type"] = "application/json";
  }
  // Note : pour FormData, on laisse le browser poser Content-Type avec le boundary

  if (!skipAuth && _accessToken) {
    finalHeaders.Authorization = `Bearer ${_accessToken}`;
  }

  const fullPath = `${path}${buildQueryString(query)}`;
  const url = fullPath.startsWith("http") ? fullPath : `${API_BASE}${fullPath}`;
  const resp = await fetch(url, {
    method,
    headers: finalHeaders,
    body: body === undefined ? undefined : isFormData ? (body as FormData) : JSON.stringify(body),
    credentials: "omit",
  });

  if (!resp.ok) {
    let problem: Record<string, unknown>;
    try {
      problem = (await resp.json()) as Record<string, unknown>;
    } catch {
      problem = { detail: await resp.text().catch(() => "") };
    }
    throw new ApiError(
      resp.status,
      (problem.type as string) ?? "",
      (problem.title as string) ?? `HTTP ${resp.status}`,
      (problem.detail as string) ?? "",
      problem.request_id as string | undefined,
      problem,
    );
  }

  if (resp.status === 204) {
    return undefined as T;
  }

  return (await resp.json()) as T;
}

// ---------------------------------------------------------------------------
// Auth endpoints
// ---------------------------------------------------------------------------
export type LoginResponse = {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
  must_change_password?: boolean;
};

export type Role = "it" | "commercial" | "finance" | "merchant_admin" | "admin";

export type MeResponse = {
  id: string;
  email: string;
  role: Role;
  merchant_id: string | null;
  first_name: string | null;
  last_name: string | null;
  must_change_password?: boolean;
};

export async function changePassword(current_password: string, new_password: string): Promise<void> {
  await apiRequest("/admin/auth/change-password", {
    method: "POST",
    body: { current_password, new_password },
  });
}

export async function login(email: string, password: string): Promise<LoginResponse> {
  const resp = await apiRequest<LoginResponse>("/admin/auth/login", {
    method: "POST",
    body: { email, password },
    skipAuth: true,
  });
  setTokens(resp.access_token, resp.refresh_token);
  return resp;
}

export async function refreshAccessToken(): Promise<LoginResponse | null> {
  const refresh = getRefreshToken();
  if (!refresh) return null;
  try {
    const resp = await apiRequest<LoginResponse>("/admin/auth/refresh", {
      method: "POST",
      body: { refresh_token: refresh },
      skipAuth: true,
    });
    setTokens(resp.access_token, resp.refresh_token);
    return resp;
  } catch {
    clearTokens();
    return null;
  }
}

export async function fetchMe(): Promise<MeResponse> {
  return apiRequest<MeResponse>("/admin/auth/me");
}

export function logout(): void {
  clearTokens();
}

// ---------------------------------------------------------------------------
// Merchants
// ---------------------------------------------------------------------------
export type Merchant = {
  id: string;
  business_name: string;
  business_email: string;
  business_phone: string | null;
  country: string;
  status: "active" | "suspended" | "closed";
  livemode: boolean;
  created_at: string;
};

export type MerchantList = {
  data: Merchant[];
  next_cursor: string | null;
  has_more: boolean;
};

export type DashboardLogin = {
  email: string;
  temp_password: string;
  login_url: string;
};

export type MerchantWithCredentials = {
  merchant: Merchant;
  api_key: string;
  api_secret: string;
  hmac_callback_secret: string;
  dashboard_login: DashboardLogin | null;
  warning: string;
};

export type CreateMerchantInput = {
  business_name: string;
  business_email: string;
  business_phone?: string;
  country?: string;
  livemode?: boolean;
  commission_rate_mobile_bps?: number;
  commission_rate_cards_bps?: number;
  refund_window_days?: number;
  callback_url?: string;
  reference_parsing_rule?: Record<string, unknown>;
};

export async function listMerchants(params: { cursor?: string; limit?: number; status?: string } = {}): Promise<MerchantList> {
  return apiRequest<MerchantList>("/admin/merchants", { query: params });
}

export async function getMerchant(id: string): Promise<Merchant> {
  return apiRequest<Merchant>(`/admin/merchants/${id}`);
}

export async function createMerchant(input: CreateMerchantInput): Promise<MerchantWithCredentials> {
  return apiRequest<MerchantWithCredentials>("/admin/merchants", {
    method: "POST",
    body: input,
  });
}

export async function rotateCredentials(id: string): Promise<MerchantWithCredentials> {
  return apiRequest<MerchantWithCredentials>(`/admin/merchants/${id}/credentials`, { method: "POST" });
}

export async function suspendMerchant(id: string): Promise<void> {
  await apiRequest(`/admin/merchants/${id}/suspend`, { method: "POST" });
}

export async function activateMerchant(id: string): Promise<void> {
  await apiRequest(`/admin/merchants/${id}/activate`, { method: "POST" });
}

// ---------------------------------------------------------------------------
// Payments
// ---------------------------------------------------------------------------
export type Payment = {
  id: string;
  merchant_id: string;
  livemode: boolean;
  amount_minor: number;
  currency: string;
  payment_method: string;
  status: string;
  external_reference: string;
  parsed_metadata: Record<string, unknown> | null;
  customer_phone: string | null;
  customer_email: string | null;
  customer_first_name: string | null;
  customer_last_name: string | null;
  failure_code: string | null;
  failure_message: string | null;
  created_at: string;
  completed_at: string | null;
  // Cross-tenant info — renseigné par le backend uniquement pour role=admin
  // via LEFT JOIN aviation_merchants (null pour un marchand qui ne voit que sa
  // propre data).
  merchant_business_name?: string | null;
  merchant_code?: string | null;
};

export type PaymentList = { data: Payment[]; next_cursor: string | null; has_more: boolean };

export async function listPayments(params: {
  cursor?: string;
  limit?: number;
  status?: string;
  payment_method?: string;
  merchant_id?: string;
  ticket_number?: string;
  reference?: string;
}): Promise<PaymentList> {
  return apiRequest<PaymentList>("/admin/payments", { query: params });
}

export async function getPaymentAdmin(id: string): Promise<Payment & Record<string, unknown>> {
  return apiRequest(`/admin/payments/${id}`);
}

// ---------------------------------------------------------------------------
// Refunds
// ---------------------------------------------------------------------------
export type RefundStatus =
  | "awaiting_validation"
  | "pending"
  | "processing"
  | "succeeded"
  | "failed"
  | "rejected";

export type Refund = {
  id: string;
  payment_id: string;
  merchant_id: string;
  amount_minor: number;
  amount?: number;
  currency: string;
  status: RefundStatus;
  reason: string | null;
  failure_code: string | null;
  failure_message: string | null;
  refunded_at: string | null;
  created_at: string;
  // Dual validation fields (migration 015) — présents mais nullables si flag OFF
  initiated_by?: string;
  initiated_by_user_id?: string | null;
  validator_user_id?: string | null;
  validator_comment?: string | null;
  validated_at?: string | null;
};

export async function listRefunds(paymentId: string): Promise<{ data: Refund[] }> {
  return apiRequest(`/admin/payments/${paymentId}/refunds`);
}

export type CreateRefundInput = {
  amount: number;
  reason?: string;
  external_reference?: string;
};

export async function createRefund(paymentId: string, input: CreateRefundInput): Promise<Refund> {
  return apiRequest(`/admin/payments/${paymentId}/refunds`, {
    method: "POST",
    body: input,
    headers: {
      "Idempotency-Key": `dashboard-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
    },
  });
}

/** File d'attente : refunds initiés en attente de validation par un 2e user. */
export async function listPendingRefunds(): Promise<{ data: Refund[] }> {
  return apiRequest(`/admin/refunds/pending`);
}

/** Valide un refund awaiting_validation → déclenche le payout. */
export async function validateRefund(refundId: string, comment?: string): Promise<Refund> {
  return apiRequest(`/admin/refunds/${refundId}/validate`, {
    method: "POST",
    body: { comment: comment ?? null },
  });
}

/** Rejette un refund awaiting_validation. Raison obligatoire (≥3 chars). */
export async function rejectRefund(refundId: string, reason: string): Promise<Refund> {
  return apiRequest(`/admin/refunds/${refundId}/reject`, {
    method: "POST",
    body: { reason },
  });
}

// ---------------------------------------------------------------------------
// Merchant portal (self-service)
// ---------------------------------------------------------------------------
export type CurrencyVolume = { currency: string; amount_minor: number };

export type MerchantOverview = {
  period_days: number;
  today: { count: number; volumes: CurrencyVolume[] };
  totals: {
    count: number;
    succeeded_count: number;
    succeeded_volumes: CurrencyVolume[];
    conversion_rate: number;
  };
  by_status: Record<string, { count: number; volumes: CurrencyVolume[] }>;
  by_method: Record<string, { count: number; volumes: CurrencyVolume[] }>;
  wallets: Array<{
    type: "mobile_money" | "cards";
    currency: string;
    balance_minor: number;
    reserved_balance_minor: number;
  }>;
};

export async function getMerchantOverview(days = 30): Promise<MerchantOverview> {
  return apiRequest(`/merchant/overview`, { query: { days } });
}

export type MerchantSettings = {
  merchant_id: string;
  business_name: string;
  business_email: string;
  business_phone: string | null;
  country: string;
  livemode: boolean;
  logo_data_url: string | null;
  callback_url: string | null;
  commission_rate_mobile_bps: number;
  commission_rate_cards_bps: number;
  refund_window_days: number;
};

export async function getMerchantSettings(): Promise<MerchantSettings> {
  return apiRequest(`/merchant/settings`);
}

export async function updateMerchantSettings(input: {
  business_phone?: string | null;
  callback_url?: string | null;
}): Promise<{ status: string }> {
  return apiRequest(`/merchant/settings`, { method: "PATCH", body: input });
}

export async function uploadMerchantLogo(file: File): Promise<{ status: string; bytes: number }> {
  const fd = new FormData();
  fd.append("file", file);
  const token = getAccessToken();
  const resp = await fetch(`${API_BASE}/merchant/logo`, {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: fd,
  });
  if (!resp.ok) {
    const problem = (await resp.json().catch(() => ({}))) as Record<string, unknown>;
    throw new ApiError(
      resp.status,
      (problem.type as string) ?? "",
      (problem.title as string) ?? `HTTP ${resp.status}`,
      (problem.detail as string) ?? "",
    );
  }
  return resp.json();
}

export async function deleteMerchantLogo(): Promise<{ status: string }> {
  return apiRequest(`/merchant/logo`, { method: "DELETE" });
}

export type TeamMember = {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  role: "merchant_admin" | "finance" | "it" | "commercial";
  status: "active" | "suspended";
  last_login_at: string | null;
  created_at: string;
};

export async function listTeam(): Promise<{ data: TeamMember[] }> {
  return apiRequest(`/merchant/team`);
}

export type InviteTeamInput = {
  email: string;
  first_name?: string;
  last_name?: string;
  role: "merchant_admin" | "finance" | "it" | "commercial";
};

export type TeamInviteResponse = {
  user_id: string;
  email: string;
  // Renvoyé UNIQUEMENT si l'email a échoué (email_sent=false). Sinon absent.
  temp_password: string | null;
  login_url: string;
  email_sent: boolean;
  warning: string;
};

export async function inviteTeamMember(input: InviteTeamInput): Promise<TeamInviteResponse> {
  return apiRequest(`/merchant/team/invite`, { method: "POST", body: input });
}

export async function updateTeamRole(userId: string, role: TeamMember["role"]): Promise<{ status: string }> {
  return apiRequest(`/merchant/team/${userId}/role`, { method: "PATCH", body: { role } });
}

export async function revokeTeamMember(userId: string): Promise<{ status: string }> {
  return apiRequest(`/merchant/team/${userId}/revoke`, { method: "POST" });
}

export async function activateTeamMember(userId: string): Promise<{ status: string }> {
  return apiRequest(`/merchant/team/${userId}/activate`, { method: "POST" });
}

export async function editTeamMember(userId: string, input: { first_name?: string; last_name?: string }): Promise<{ status: string }> {
  return apiRequest(`/merchant/team/${userId}`, { method: "PATCH", body: input });
}

export async function deleteTeamMember(userId: string): Promise<{ status: string }> {
  return apiRequest(`/merchant/team/${userId}`, { method: "DELETE" });
}

// ---------------------------------------------------------------------------
// Moko team (cross-tenant, admin uniquement)
// ---------------------------------------------------------------------------
export type MokoRole = "admin" | "finance" | "support" | "it" | "commercial";

export type MokoTeamMember = {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  phone_number: string | null;
  role: MokoRole;
  status: "active" | "suspended";
  totp_enabled: boolean;
  must_change_password: boolean;
  last_login_at: string | null;
  created_at: string;
};

export async function listMokoTeam(): Promise<{ data: MokoTeamMember[] }> {
  return apiRequest(`/admin/moko-team`);
}

export type InviteMokoTeamInput = {
  email: string;
  first_name?: string;
  last_name?: string;
  phone_number?: string;
  role: MokoRole;
};

export type MokoTeamInviteResponse = {
  user_id: string;
  email: string;
  temp_password: string;
  login_url: string;
  email_sent: boolean;
  warning: string;
};

export async function inviteMokoTeamMember(input: InviteMokoTeamInput): Promise<MokoTeamInviteResponse> {
  return apiRequest(`/admin/moko-team/invite`, { method: "POST", body: input });
}

export async function updateMokoTeamRole(userId: string, role: MokoRole): Promise<{ user_id: string; role: MokoRole; changed: boolean }> {
  return apiRequest(`/admin/moko-team/${userId}/role`, { method: "PATCH", body: { role } });
}

export async function revokeMokoTeamMember(userId: string): Promise<{ user_id: string; status: string }> {
  return apiRequest(`/admin/moko-team/${userId}/revoke`, { method: "POST" });
}

export async function reactivateMokoTeamMember(userId: string): Promise<{ user_id: string; status: string; changed?: boolean }> {
  return apiRequest(`/admin/moko-team/${userId}/reactivate`, { method: "POST" });
}

export type ApiKeyInfo = {
  id: string;
  api_key: string;
  api_secret_last4: string;
  livemode: boolean;
  status: "active" | "rotating" | "revoked";
  created_at: string;
  last_used_at: string | null;
  revoked_at: string | null;
};

export async function listMerchantApiKeys(): Promise<{ data: ApiKeyInfo[] }> {
  return apiRequest(`/merchant/api-keys`);
}

export type RotatedApiKey = {
  api_key: string;
  api_secret: string;
  hmac_callback_secret: string;
  warning: string;
};

export async function rotateMerchantApiKey(): Promise<RotatedApiKey> {
  return apiRequest(`/merchant/api-keys/rotate`, { method: "POST" });
}

// ---------------------------------------------------------------------------
// Merchant — wallet movements (statements + history)
// ---------------------------------------------------------------------------
export type WalletMovementFull = {
  id: string;
  wallet_id: string;
  merchant_id: string;
  type: string;
  // Human-readable label calculé côté API (ex: "Transfer to Bank" pour les W2B).
  // Cf ADR-0018. Fallback : mapping local (labelType) si absent.
  display_type?: string;
  amount_minor: number;
  balance_after_minor: number;
  currency: string;
  reference_type: string;
  reference_id: string;
  description: string | null;
  created_at: string;
  wallet_type: "mobile_money" | "cards";
  wallet_currency: string;
  // Cross-tenant enrichment — renseigné pour Moko staff, null pour merchant
  merchant_business_name?: string | null;
  merchant_code?: string | null;
};

export async function listMovements(params: {
  wallet_id?: string;
  currency?: string;
  movement_type?: string;
  date_from?: string;
  date_to?: string;
  cursor?: string;
  limit?: number;
  merchant_id_filter?: string;
} = {}): Promise<{ data: WalletMovementFull[]; next_cursor: string | null; has_more: boolean }> {
  return apiRequest(`/merchant/movements`, { query: params });
}

// Cf ADR-0018 : un settlement = 1 Wallet-to-Bank transfer exécuté par la
// Finance Moko via console.gofreshpay.com/finance/transfer/w2b, mirroré dans
// aviation_wallet_movements.
export type Settlement = {
  id: string;
  object: "settlement";
  merchant_id: string;
  reference: string;                // aviation-w2b-<timestamp>
  wallet_type: "mobile_money" | "cards";
  currency: string;
  amount_minor: number;             // toujours > 0
  amount: number;                   // amount_minor / 100
  status: "paid";
  description: string | null;
  executed_at: string;              // ISO datetime
  // Cross-tenant enrichment — renseigné pour Moko staff, null pour merchant
  merchant_business_name?: string | null;
  merchant_code?: string | null;
};

export async function listSettlements(params: {
  cursor?: string;
  limit?: number;
  currency?: string;
  wallet_type?: "mobile_money" | "cards";
  merchant_id_filter?: string;
} = {}): Promise<{
  data: Settlement[];
  next_cursor: string | null;
  has_more: boolean;
}> {
  return apiRequest(`/merchant/settlements`, { query: params });
}

// Solde d'ouverture / clôture par wallet par jour.
// Alimenté par le cron /opt/moko-aviation/scripts/daily_balances.sh
// (cf migration 016). Source de vérité finance pour le cycle quotidien.
export type DailyBalance = {
  wallet_id: string;
  balance_date: string;                          // YYYY-MM-DD
  wallet_type: "mobile_money" | "cards";
  currency: string;
  opening_balance_minor: number;
  closing_balance_minor: number | null;          // null tant que la journée n'est pas clôturée
  credits_minor: number;
  debits_minor: number;
  w2b_minor: number;
  movements_count: number;
  merchant_id: string;
  merchant_business_name?: string | null;
};

export async function listDailyBalances(params: {
  date_from?: string;
  date_to?: string;
  wallet_type?: "mobile_money" | "cards";
  currency?: string;
  limit?: number;
} = {}): Promise<{ data: DailyBalance[] }> {
  return apiRequest(`/merchant/daily-balances`, { query: params });
}

export async function resendPaymentCallback(paymentId: string): Promise<{ status: string; attempt_id: string; mode: string }> {
  return apiRequest(`/admin/payments/${paymentId}/resend-callback`, { method: "POST" });
}

// ---------------------------------------------------------------------------
// Wallets
// ---------------------------------------------------------------------------
export type Wallet = {
  id: string;
  merchant_id: string;
  business_name: string;
  type: "mobile_money" | "cards";
  currency: string;
  balance_minor: number;
  reserved_balance_minor: number;
  updated_at: string;
};

export async function listWallets(params: { merchant_id?: string } = {}): Promise<{ data: Wallet[] }> {
  return apiRequest<{ data: Wallet[] }>("/admin/wallets", { query: params });
}

export type WalletMovement = {
  id: string;
  type: string;
  amount_minor: number;
  balance_after_minor: number;
  reference_type: string;
  reference_id: string;
  description: string | null;
  created_at: string;
};

export async function listWalletMovements(walletId: string, params: { cursor?: string; limit?: number } = {}): Promise<{ data: WalletMovement[]; next_cursor: string | null; has_more: boolean }> {
  return apiRequest(`/admin/wallets/${walletId}/movements`, { query: params });
}

// ---------------------------------------------------------------------------
// Switch logs / Webhooks / Audit
// ---------------------------------------------------------------------------
export type SwitchLog = {
  id: number;
  request_id: string | null;
  merchant_id: string | null;
  payment_id: string | null;
  refund_id: string | null;
  switch_name: string;
  operation: string;
  direction: "out" | "in";
  request_url: string | null;
  response_status: number | null;
  duration_ms: number | null;
  error: string | null;
  created_at: string;
};

export async function listSwitchLogs(params: {
  cursor?: number;
  limit?: number;
  switch_name?: string;
  merchant_id?: string;
  operation?: string;
  direction?: string;
  error_only?: boolean;
}): Promise<{ data: SwitchLog[]; next_cursor: number | null; has_more: boolean }> {
  return apiRequest("/admin/switch-logs", { query: params });
}

export type WebhookAttempt = {
  id: string;
  merchant_id: string;
  event_type: string;
  event_id: string;
  url: string;
  status: "pending" | "succeeded" | "failed" | "retrying" | "abandoned";
  attempt_count: number;
  last_status_code: number | null;
  last_error: string | null;
  next_retry_at: string | null;
  completed_at: string | null;
  created_at: string;
};

export async function listWebhookAttempts(params: { cursor?: string; limit?: number; status?: string; merchant_id?: string }): Promise<{ data: WebhookAttempt[]; next_cursor: string | null; has_more: boolean }> {
  return apiRequest("/admin/webhook-attempts", { query: params });
}

export async function replayWebhook(id: string): Promise<void> {
  await apiRequest(`/admin/webhook-attempts/${id}/replay`, { method: "POST" });
}

// ---------------------------------------------------------------------------
// Funnel stats
// ---------------------------------------------------------------------------
export type FunnelStats = {
  period_days: number;
  by_status: Record<string, { count: number; volumes: CurrencyVolume[] }>;
};

export async function getFunnelStats(params: { merchant_id?: string; days?: number } = {}): Promise<FunnelStats> {
  return apiRequest("/admin/stats/funnel", { query: params });
}

// ---------------------------------------------------------------------------
// Audit log
// ---------------------------------------------------------------------------
export type AuditEntry = {
  id: number;
  user_id: string | null;
  merchant_id: string | null;
  action: string;
  resource_type: string | null;
  resource_id: string | null;
  ip_address: string | null;
  created_at: string;
};

export async function listAuditLog(params: { cursor?: number; limit?: number; action?: string; merchant_id?: string; user_id?: string }): Promise<{ data: AuditEntry[]; next_cursor: number | null; has_more: boolean }> {
  return apiRequest("/admin/audit-log", { query: params });
}

// ---------------------------------------------------------------------------
// Notifications
// ---------------------------------------------------------------------------
export type NotificationSeverity = "info" | "success" | "warning" | "danger";

export type Notification = {
  id: string;
  object: "notification";
  type: string;
  severity: NotificationSeverity;
  title: string;
  body: string;
  action_url: string | null;
  metadata: Record<string, unknown> | null;
  read_at: string | null;
  created_at: string;
};

export async function listNotifications(params: {
  unread_only?: boolean;
  cursor?: string;
  limit?: number;
} = {}): Promise<{ data: Notification[]; next_cursor: string | null; has_more: boolean }> {
  return apiRequest("/admin/notifications", { query: params });
}

export async function getUnreadNotificationCount(): Promise<{ unread: number }> {
  return apiRequest("/admin/notifications/unread-count");
}

export async function markNotificationRead(notificationId: string): Promise<void> {
  await apiRequest(`/admin/notifications/${notificationId}/read`, { method: "POST" });
}

export async function markAllNotificationsRead(): Promise<void> {
  await apiRequest("/admin/notifications/read-all", { method: "POST" });
}

// ---------------------------------------------------------------------------
// Support tickets (proxy vers freshpay_online.support_tickets)
// ---------------------------------------------------------------------------
export type TicketStatus = "open" | "in_progress" | "resolved" | "closed";
export type TicketPriority = "low" | "medium" | "high" | "urgent";

export type TicketAttachment = {
  name: string;
  type: string;
  size: number;
  data_url: string;
};

export type TicketMessage = {
  id: number;
  ticket_id: string;
  sender_type: "user" | "staff";
  sender_name: string | null;
  message: string;
  attachments: TicketAttachment[] | null;
  created_at: string;
};

export type TicketSummary = {
  id: number;
  ticket_id: string;
  subject: string;
  status: TicketStatus;
  priority: TicketPriority;
  category: string;
  created_by: string;
  created_at: string;
  updated_at: string | null;
  message_count: number;
  unread_by_merchant: boolean;
  last_message_at: string | null;
};

export type TicketDetail = TicketSummary & {
  description: string;
  email: string | null;
  messages: TicketMessage[];
};

export async function listTickets(params: {
  status?: TicketStatus | "all";
  priority?: TicketPriority | "all";
  page?: number;
  per_page?: number;
} = {}): Promise<{
  data: TicketSummary[];
  pagination: { page: number; per_page: number; total: number; total_pages: number };
}> {
  return apiRequest("/admin/tickets", { query: params });
}

export async function getTicket(ticketId: string, opts: { mark_viewed?: boolean } = {}): Promise<TicketDetail> {
  return apiRequest(`/admin/tickets/${ticketId}`, {
    query: { mark_viewed: opts.mark_viewed },
  });
}

export async function createTicket(input: {
  subject: string;
  description: string;
  priority?: TicketPriority;
  category?: string;
  attachments?: TicketAttachment[];
}): Promise<TicketDetail> {
  return apiRequest("/admin/tickets", { method: "POST", body: input });
}

export async function addTicketMessage(
  ticketId: string,
  input: { message: string; attachments?: TicketAttachment[] },
): Promise<TicketDetail> {
  return apiRequest(`/admin/tickets/${ticketId}/messages`, {
    method: "POST",
    body: input,
  });
}

export async function markTicketViewed(ticketId: string): Promise<void> {
  await apiRequest(`/admin/tickets/${ticketId}/mark-viewed`, { method: "POST" });
}

// ============================================================================
// Card refund batches (Equity acquirer) — cf ADR-0020, migration 018
// ============================================================================
export type CardRefundPending = {
  refund_id: string;
  payment_id: string;
  merchant_id: string;
  merchant_business_name: string | null;
  amount_minor: number;
  currency: string;
  reason: string | null;
  external_reference: string | null;
  created_at: string;
  payment_method: "visa" | "mastercard";
  payment_switch_request_id: string | null;
  payment_external_reference: string | null;
  customer_email: string | null;
  customer_name: string;
  internal_export_deadline: string | null;
};

export type RefundBatch = {
  id: string;
  generated_by_user_id: string;
  generated_at: string;
  refund_count: number;
  total_amount_minor_by_currency: Record<string, number> | null;
  export_file_path: string | null;
  export_file_sha256: string | null;
  equity_response_file_path: string | null;
  equity_response_imported_at: string | null;
  equity_response_imported_by: string | null;
  status: "open" | "exported" | "partially_processed" | "closed";
  notes: string | null;
};

export type BatchRefund = {
  refund_id: string;
  payment_id: string;
  amount_minor: number;
  currency: string;
  status: string;
  reason: string | null;
  external_reference: string | null;
  created_at: string;
  acquirer_reference: string | null;
  acquirer_response_at: string | null;
  acquirer_response_notes: string | null;
  payment_switch_request_id: string | null;
  payment_external_reference: string | null;
};

export async function listPendingCardRefunds(): Promise<{ data: CardRefundPending[]; count: number }> {
  return apiRequest("/admin/card-refunds/pending");
}

export async function createRefundBatch(input: { refund_ids: string[]; notes?: string }): Promise<RefundBatch> {
  return apiRequest("/admin/refund-batches", { method: "POST", body: input });
}

export async function listRefundBatches(params: { status?: string; limit?: number } = {}): Promise<{ data: RefundBatch[]; count: number }> {
  return apiRequest("/admin/refund-batches", { query: params });
}

export async function getRefundBatch(batchId: string): Promise<{ batch: RefundBatch; refunds: BatchRefund[] }> {
  return apiRequest(`/admin/refund-batches/${batchId}`);
}

// Note : download CSV utilise directement une <a href> pour bénéficier du Content-Disposition
export function refundBatchCsvUrl(batchId: string): string {
  return `/admin/refund-batches/${batchId}/csv`;
}

export async function importBatchResponse(batchId: string, file: File): Promise<{
  batch_id: string;
  batch_status: string;
  processed: { succeeded: number; failed: number; skipped: number; done: number; total: number };
}> {
  const formData = new FormData();
  formData.append("file", file);
  return apiRequest(`/admin/refund-batches/${batchId}/import-response`, {
    method: "POST",
    body: formData,
    // apiRequest should detect FormData and skip JSON serialization + Content-Type
  });
}
